import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  Bell, 
  Globe, 
  Shield, 
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  MessageCircle,
  Phone,
  Search,
  Sparkles,
  Video,
  Home,
  Building2,
  Play,
  Bot
} from 'lucide-react';
import axios from 'axios';
import BookingAssistant from '../components/BookingAssistant';
import VoiceInterfaceCombined from '../components/VoiceInterfaceCombined';
import { useActiveAssistant } from '../hooks/useActiveAssistant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Landing = () => {
  const { t, isRTL, locale } = useLanguage();
  const navigate = useNavigate();
  const activeAssistantId = useActiveAssistant();
  const [stats, setStats] = useState({ professionals: 0, appointments: 0 });

  useEffect(() => {
    // Seed database on first load
    const seedAndFetch = async () => {
      try {
        await axios.post(`${API_URL}/api/seed`);
        const response = await axios.get(`${API_URL}/api/statistics`).catch(() => null);
        if (response?.data) {
          setStats({
            professionals: response.data.total_professionals || 0,
            appointments: response.data.total_appointments || 0
          });
        }
      } catch (error) {
        console.log('Stats fetch error (non-critical):', error);
      }
    };
    seedAndFetch();
  }, []);

  const features = [
    {
      icon: Calendar,
      title: t('feature_instant_booking'),
      description: t('feature_instant_booking_desc'),
      color: 'feature-icon-primary'
    },
    {
      icon: Bell,
      title: t('feature_smart_waitlist'),
      description: t('feature_smart_waitlist_desc'),
      color: 'feature-icon-secondary'
    },
    {
      icon: Globe,
      title: t('feature_multi_language'),
      description: t('feature_multi_language_desc'),
      color: 'feature-icon-accent'
    },
    {
      icon: Shield,
      title: t('feature_secure'),
      description: t('feature_secure_desc'),
      color: 'feature-icon-primary'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative hero-gradient noise-overlay py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center ${isRTL ? 'md:grid-flow-col-dense' : ''}`}>
            {/* Text Content - Spans 7 columns */}
            <div className={`md:col-span-7 space-y-6 ${isRTL ? 'md:col-start-6' : ''}`}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight animate-fade-in" data-testid="hero-title">
                {t('hero_title')}
              </h1>
              <p className="text-base md:text-lg text-slate-600 max-w-xl animate-slide-up" style={{ animationDelay: '0.1s' }} data-testid="hero-subtitle">
                {t('hero_subtitle')}
              </p>
              <div className={`flex flex-col gap-4 animate-slide-up ${isRTL ? 'items-end' : 'items-start'}`} style={{ animationDelay: '0.2s' }}>
                {/* Patient Button */}
                <div className="flex flex-col items-start">
                  <Button 
                    size="lg" 
                    className="btn-shine rounded-full px-6 py-4 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                    onClick={() => navigate('/register')}
                    data-testid="cta-patient"
                  >
                    {t('hero_cta_patient')}
                    <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
                  </Button>
                  <span className="text-sm text-slate-500 mt-1 ml-2">{t('hero_cta_patient_desc')}</span>
                </div>
                
                {/* Professional Button */}
                <div className="flex flex-col items-start">
                  <Button 
                    size="lg" 
                    className="rounded-full px-6 py-4 bg-red-500 hover:bg-red-600 text-white border-0 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                    onClick={() => navigate('/register?type=professional')}
                    data-testid="cta-professional"
                  >
                    {t('hero_cta_professional')}
                  </Button>
                  <span className="text-sm text-slate-500 mt-1 ml-2">{t('hero_cta_professional_desc')}</span>
                </div>
                
                {/* Laboratory Button */}
                <div className="flex flex-col items-start">
                  <Button 
                    size="lg" 
                    className="rounded-full px-6 py-4 bg-blue-500 hover:bg-blue-600 text-white border-0 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                    onClick={() => navigate('/register/laboratoire')}
                    data-testid="cta-labo"
                  >
                    {t('hero_cta_lab_rep')}
                  </Button>
                  <span className="text-sm text-slate-500 mt-1 ml-2">{t('hero_cta_lab_desc')}</span>
                </div>
              </div>
              
              {/* Advanced Search Link */}
              <div className="animate-slide-up" style={{ animationDelay: '0.25s' }}>
                <Button 
                  variant="ghost" 
                  className="text-slate-600 hover:text-primary gap-2 p-0"
                  onClick={() => navigate('/search')}
                  data-testid="link-advanced-search"
                >
                  <Search className="w-4 h-4" />
                  {t('advanced_search')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Stats */}
              <div className={`flex gap-8 pt-4 animate-slide-up ${isRTL ? 'flex-row-reverse' : ''}`} style={{ animationDelay: '0.3s' }}>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{stats.professionals}+</div>
                  <div className="text-sm text-slate-500">{t('nav_professionals')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">{stats.appointments}+</div>
                  <div className="text-sm text-slate-500">{t('dashboard_appointments')}</div>
                </div>
              </div>
            </div>

            {/* Image - Spans 5 columns */}
            <div className={`md:col-span-5 ${isRTL ? 'md:col-start-1' : ''}`}>
              <div className="relative animate-scale-in" style={{ animationDelay: '0.2s' }}>
                <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-3xl transform -rotate-6"></div>
                <img 
                  src="https://images.unsplash.com/photo-1673865641073-4479f93a7776?w=600&h=700&fit=crop"
                  alt="Doctor"
                  className="relative rounded-2xl shadow-2xl w-full object-cover"
                  style={{ aspectRatio: '4/5' }}
                />
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{t('status_confirmed')}</div>
                      <div className="text-xs text-slate-500">14:00 - Dr. Martin</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EU Teleconsultation Advantages - TOP 3 */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden" data-testid="eu-advantages-section">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-yellow-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-yellow-400 text-yellow-900 mb-4 px-4 py-1">
              <Sparkles className="w-4 h-4 mr-2" />
              {t('eu_marketplace_badge') || 'Marketplace Européenne'}
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {t('eu_teleconsult_title') || 'Téléconsultation sans frontières'}
            </h2>
            <p className="text-blue-100 max-w-2xl mx-auto text-lg">
              {t('eu_teleconsult_subtitle') || 'Le médecin de votre choix en téléconsultation, partout en Europe'}
            </p>
          </div>

          {/* 3 Main Advantages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Advantage 1: Voice Assistant 24/7 */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white hover:bg-white/20 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-yellow-400 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Phone className="w-10 h-10 text-yellow-900" />
                </div>
                <h3 className="font-bold text-xl mb-3">{t('eu_voice_title')}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {t('eu_voice_desc')}
                </p>
                <Badge className="mt-4 bg-yellow-400/20 text-yellow-200 border-yellow-400/30">
                  <Clock className="w-3 h-3 mr-1" />
                  RDV intelligent automatique
                </Badge>
              </CardContent>
            </Card>

            {/* Advantage 2: EU-Wide Teleconsultation */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white hover:bg-white/20 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-green-400 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <Globe className="w-10 h-10 text-green-900" />
                </div>
                <h3 className="font-bold text-xl mb-3">{t('eu_cross_border_title')}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {t('eu_cross_border_desc')}
                </p>
                <Badge className="mt-4 bg-green-400/20 text-green-200 border-green-400/30">
                  <Video className="w-3 h-3 mr-1" />
                  Téléconsultation HD
                </Badge>
              </CardContent>
            </Card>

            {/* Advantage 3: Language & Specialty Matching */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 text-white hover:bg-white/20 transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-purple-400 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-10 h-10 text-purple-900" />
                </div>
                <h3 className="font-bold text-xl mb-3">{t('eu_matching_title')}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {t('eu_matching_desc')}
                </p>
                <Badge className="mt-4 bg-purple-400/20 text-purple-200 border-purple-400/30">
                  <Users className="w-3 h-3 mr-1" />
                  Filtrage intelligent
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              size="lg" 
              className="rounded-full px-8 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-semibold shadow-xl"
              onClick={() => navigate('/consultations/search')}
              data-testid="cta-teleconsultation"
            >
              <Video className="w-5 h-5 mr-2" />
              Trouver un médecin en ligne
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('feature_instant_booking')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('feature_instant_booking_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="card-hover border-slate-200"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className={`feature-icon ${feature.color} mb-4`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('how_it_works')}
            </h2>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 ${isRTL ? 'md:grid-flow-col-dense' : ''}`}>
            {[
              { icon: Users, title: t('step1_title'), desc: t('step1_desc') },
              { icon: Calendar, title: t('step2_title'), desc: t('step2_desc') },
              { icon: Clock, title: t('step3_title'), desc: t('step3_desc') }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Assistant Section - WhatsApp & Voice */}
      <section className="py-16 md:py-24 bg-white" data-testid="booking-assistant-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <BookingAssistant />
        </div>
      </section>

      {/* Consultation Types Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="consultation-types-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('consultation_type.select')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('feature_instant_booking_desc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* In Office */}
            <Card className="card-hover border-slate-200 overflow-hidden group cursor-pointer" onClick={() => navigate('/search?type=cabinet')}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{t('consultation_type.cabinet')}</h3>
                <p className="text-sm text-slate-600">{t('feature_instant_booking_desc')}</p>
                <Badge className="mt-3 bg-blue-100 text-blue-700 hover:bg-blue-100">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {t('feature_instant_booking')}
                </Badge>
              </CardContent>
            </Card>
            
            {/* Online / Teleconsultation */}
            <Card className="card-hover border-slate-200 overflow-hidden group cursor-pointer relative" onClick={() => navigate('/search?type=teleconsultation')}>
              <div className="absolute top-3 right-3">
                <Badge className="bg-green-500 text-white">Popular</Badge>
              </div>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                  <Video className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{t('consultation_type.online')}</h3>
                <p className="text-sm text-slate-600">{t('teleconsultation.title')}</p>
                <Badge className="mt-3 bg-green-100 text-green-700 hover:bg-green-100">
                  <Video className="w-3 h-3 mr-1" />
                  WebRTC HD
                </Badge>
              </CardContent>
            </Card>
            
            {/* Home Visit */}
            <Card className="card-hover border-slate-200 overflow-hidden group cursor-pointer relative" onClick={() => navigate('/search?type=home')}>
              <div className="absolute top-3 right-3">
                <Badge className="bg-orange-500 text-white">New</Badge>
              </div>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                  <Home className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{t('consultation_type.home')}</h3>
                <p className="text-sm text-slate-600">{t('home_visit.description')}</p>
                <Badge className="mt-3 bg-orange-100 text-orange-700 hover:bg-orange-100">
                  <Home className="w-3 h-3 mr-1" />
                  {t('home_visit.title')}
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Video Tutorials Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="tutorials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('tutorials.title')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('ask_ai.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Patient Tutorial */}
            <Card className="overflow-hidden border-slate-200">
              <div className="relative aspect-video bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center group cursor-pointer">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="relative z-10 text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <h3 className="font-semibold text-xl">{t('tutorials.patient')}</h3>
                  <p className="text-white/80 text-sm mt-2">{t('tutorials.watch')}</p>
                </div>
                {/* Placeholder for actual YouTube embed */}
                <div className="absolute bottom-3 right-3">
                  <Badge className="bg-red-600 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    YouTube
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h4 className="font-semibold text-slate-900">{t('tutorials.patient')}</h4>
                <p className="text-sm text-slate-600 mt-1">{t('step1_desc')} → {t('step2_desc')} → {t('step3_desc')}</p>
              </CardContent>
            </Card>
            
            {/* Professional Tutorial */}
            <Card className="overflow-hidden border-slate-200">
              <div className="relative aspect-video bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center group cursor-pointer">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="relative z-10 text-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <h3 className="font-semibold text-xl">{t('tutorials.professional')}</h3>
                  <p className="text-white/80 text-sm mt-2">{t('tutorials.watch')}</p>
                </div>
                <div className="absolute bottom-3 right-3">
                  <Badge className="bg-red-600 text-white">
                    <Play className="w-3 h-3 mr-1" />
                    YouTube
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h4 className="font-semibold text-slate-900">{t('tutorials.professional')}</h4>
                <p className="text-sm text-slate-600 mt-1">{t('feature_instant_booking_desc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('pricing_trial')}
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            {t('hero_subtitle')}
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            className="rounded-full px-8"
            onClick={() => navigate('/register')}
            data-testid="cta-final"
          >
            {t('nav_register')}
            <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
          </Button>
        </div>
      </section>
      
      {/* Voice Assistant Floating Button with Fallback */}
      <VoiceInterfaceCombined 
        assistantId={activeAssistantId}
        preferredLanguage={locale}
        onError={(e) => console.error('Voice error:', e.message)}
      />
      
    </div>
  );
};

export default Landing;
